import React from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { useRequestProviderContext } from '@common/react/components/RequestProvider/RequestProvider';

import CustomRequestProvider from '@app/components/CustomRequestProvider/CustomRequestProvider';
import SearchField from '@app/components/Forms/SearchField/SearchField';
import ScrollToTop from '@app/components/LocalCommon/ScrollToTop/ScrollToTop';
import {
	BOOKING,
	BOOKING_APPOINTMENT,
	BOOKING_ARRANGEMENTS,
	BOOKING_CONFIRMATION,
	BOOKING_FAMILY,
	BOOKING_PAYMENT,
	BOOKING_PET,
	BOOKING_PHONE_VERIFICATION,
	BOOKING_SCHEDULE,
	bookingRoutes,
	LS_BOOKING,
	LS_CONTINUE_SESSION,
	LS_ERROR_CODE,
	LS_EXISTING_PET_ENTRY,
	LS_FAMILY_STEP,
	LS_ONLINE_BOOKING_TRACKING,
	LS_RESERVATION_START_TIME,
	LS_SCHEDULE_STEP,
	LS_SELECTED_OPTION,
	LS_STEP_PET,
} from '@app/components/LocalCommon/Utils/booking';
import { scrollToTop } from '@app/components/LocalCommon/Utils/scrollToTop';
import BookingErrorScreen from '@app/components/Pages/Booking/BookingErrorScreen';
import BookingStep from '@app/components/Pages/Booking/BookingStep';
import BookingFAQ from '@app/components/UI/Booking/BookingFAQ';
import BookingReservAppointment from '@app/components/UI/Booking/BookingReservAppointment';
import BookingLoader from '@app/components/UI/BookingLoader/BookingLoader';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import {
	AppointmentParentType,
	ExistingOnlineBookingAppointmentResponse,
	FamilyStep,
	OnlineBookingEntry,
	OnlineBookingLastStep,
	ScheduleOption,
} from '@app/objects/OnlineBooking';
import '@app/scss/components/booking.scss';

export enum BookingErrorCode {
	ValidationError = 65,
	UnableToConfirm = 66,
	RestrictedLocation = 67,
	InvalidSession = 68,
	MultipleFamilyMatches = 69,
	DidNotReceiveCode = 70,
	NotAvailable = 71, // it doesn't come from the backend
	PetRequiresAttention = 72,
	LargePet = 73,
	TimeNoLongerAvailable = 74,
	PaymentIssue = 75,
	PermanentPaymentIssue = 76,
	PetHasExistingAppointment = 85,
	InvalidFamily = 86,
	NoAvailableAssistance = 87,
	IgnoredForAllVetsPetType = 88,
	IncompleteBookingData = 89,
	RequestCallBack = 152, // it doesn't come from the backend
}

export interface BookingContextProps {
	state: {
		item: OnlineBookingEntry | undefined;
		errorCode: BookingErrorCode | null;
		existingPetEntry: ExistingOnlineBookingAppointmentResponse | null;
		selectedOption: ScheduleOption | null;
		isLoadingReserve: boolean;
		errorMessageReserve: string;
	};
	action: {
		setItem: (val: OnlineBookingEntry | undefined) => void;
		setErrorCode: (code: BookingErrorCode | null) => void;
		setExistingPetEntry: (val: ExistingOnlineBookingAppointmentResponse | null) => void;
		setSelectedOption: (val: ScheduleOption | null) => void;
		setIsLoadingReserve: (bool: boolean) => void;
		setErrorMessageReserve: (str: string) => void;
	};
}

export const BookingContext = React.createContext<BookingContextProps | null>(null);

export const resetSavedLocalData = (navigate: NavigateFunction, path = BOOKING) => {
	localStorage.removeItem(LS_BOOKING);
	localStorage.removeItem(LS_FAMILY_STEP);
	localStorage.removeItem(LS_STEP_PET);
	localStorage.removeItem(LS_SCHEDULE_STEP);
	localStorage.removeItem(LS_SELECTED_OPTION);
	localStorage.removeItem(LS_ERROR_CODE);
	localStorage.removeItem(LS_RESERVATION_START_TIME);
	localStorage.removeItem(LS_CONTINUE_SESSION);
	localStorage.removeItem(LS_EXISTING_PET_ENTRY);
	localStorage.removeItem(LS_ONLINE_BOOKING_TRACKING);
	navigate(path);
};

interface Response {
	success: boolean;
	error?: string;
	errorCode?: BookingErrorCode;
}

const errorCodeNotEndingSession: Array<BookingErrorCode> = [
	BookingErrorCode.ValidationError,
	BookingErrorCode.TimeNoLongerAvailable,
	BookingErrorCode.PaymentIssue,
	BookingErrorCode.PermanentPaymentIssue,
	BookingErrorCode.PetHasExistingAppointment,
	BookingErrorCode.IncompleteBookingData,
];

const BookingCheckZip: React.FC = () => {
	const requestContext = useRequestProviderContext();
	const navigate = useNavigate();
	const location = useLocation();
	const [firstRender, setFirstRender] = React.useState<boolean>(true);

	const [valueZip, setValueZip] = React.useState<string | undefined>('');

	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>('');
	const [errorCode, setErrorCode] = React.useState<BookingErrorCode | null>(null);
	const [booking, setBooking] = React.useState<OnlineBookingEntry | undefined>(undefined);
	const [existingPetEntry, setExistingPetEntry] = React.useState<ExistingOnlineBookingAppointmentResponse | null>(null);
	const [selectedOption, setSelectedOption] = React.useState<ScheduleOption | null>(null);
	const [isLoadingReserve, setIsLoadingReserve] = React.useState<boolean>(false);
	const [errorMessageReserve, setErrorMessageReserve] = React.useState<string>('');

	const resetSavedData = () => {
		setBooking(undefined);
		setValueZip(undefined);
		setSelectedOption(null);
		setExistingPetEntry(null);
		resetSavedLocalData(navigate);
	};

	const redirect = (localErrorCode = errorCode) => {
		const localBooking: OnlineBookingEntry | undefined = localStorage.getItem(LS_BOOKING) ? JSON.parse(localStorage[LS_BOOKING]) : undefined;
		const localFamilyStep: FamilyStep | undefined = localStorage.getItem(LS_FAMILY_STEP) ? JSON.parse(localStorage[LS_FAMILY_STEP]) : undefined;
		const pathname = location.pathname;

		if (localBooking) {
			setBooking({ ...localBooking });

			if (pathname === BOOKING) {
				return resetSavedData();
			}

			if (localBooking.lastStep === OnlineBookingLastStep.Confirmation) {
				return navigate(BOOKING_CONFIRMATION);
			}

			if (localBooking.selectedAppointmentType === AppointmentParentType.Hospice && pathname === BOOKING_ARRANGEMENTS) {
				return navigate(BOOKING_APPOINTMENT);
			}

			if (localErrorCode === null) {
				for (const key in bookingRoutes) {
					if (
						(
							pathname === BOOKING_PHONE_VERIFICATION
							&& localFamilyStep !== FamilyStep.FamilyForm
							&& localBooking.lastStep === OnlineBookingLastStep.Family
						) || (
							bookingRoutes[key] === pathname
							&& localFamilyStep === FamilyStep.FamilyForm
							&& Number(key) <= localBooking.lastStep
						)
					) {
						return;
					}
				}
			}

			switch (localBooking.lastStep) {
				case OnlineBookingLastStep.Family:
					if (localFamilyStep !== FamilyStep.FamilyForm) {
						return navigate(BOOKING_PHONE_VERIFICATION);
					}
					return navigate(BOOKING_FAMILY);
				case OnlineBookingLastStep.Pet:
					return navigate(BOOKING_PET);
				case OnlineBookingLastStep.Appointment:
					return navigate(BOOKING_APPOINTMENT);
				case OnlineBookingLastStep.Arrangements:
					return navigate(BOOKING_ARRANGEMENTS);
				case OnlineBookingLastStep.Schedule:
					return navigate(BOOKING_SCHEDULE);
				case OnlineBookingLastStep.Payment:
					return navigate(BOOKING_PAYMENT);
				default:
					return resetSavedData();
			}
		}
		return resetSavedData();
	};

	const checkZipCode = () => {
		setLoading(true);
		setError('');
		setErrorCode(null);

		requestContext.state.request<Response | OnlineBookingEntry>(`booking/zip/${valueZip}`)
			.then((res: any) => {
				if (res?.errorCode && res.errorCode !== BookingErrorCode.ValidationError) {
					setErrorCode(res.errorCode);

					if (res.errorCode === BookingErrorCode.RestrictedLocation) {
						navigate(`/find-a-vet/search?query=${valueZip}`);
					}
				} else if (res?.error?.length || !res) {
					setError(res?.error || 'Service unavailable. Please try again later.');
				} else if (res?.guid) {
					if (res?.telehospiceOnly) {
						navigate(`/find-a-vet/search?query=${valueZip}`);
						return;
					}

					navigate(BOOKING_PHONE_VERIFICATION);
					setBooking(res);
				}
			})
			.catch((err) => setError(err))
			.finally(() => setLoading(false));
	};

	React.useEffect(() => {
		if (localStorage.getItem(LS_BOOKING)) {
			const localErrorCode: BookingErrorCode | null = localStorage.getItem(LS_ERROR_CODE) ? JSON.parse(localStorage[LS_ERROR_CODE]) : null;

			if (localErrorCode === null || errorCodeNotEndingSession.some((code) => code === localErrorCode)) {
				setErrorCode(localErrorCode);
				redirect(localErrorCode);
			} else {
				resetSavedData();
			}
		} else {
			resetSavedData();
		}

		setFirstRender(false);
	}, []);

	React.useEffect(() => {
		if (valueZip?.length) {
			checkZipCode();
		}
	}, [valueZip]);

	React.useEffect(() => {
		if (!firstRender) {
			if (booking) {
				localStorage.setItem(LS_BOOKING, JSON.stringify(booking));

				if (location.pathname === BOOKING) {
					resetSavedData();
				}
			} else {
				resetSavedData();
			}
		}
	}, [booking]);

	React.useEffect(() => {
		if (!firstRender) {
			if (errorCode === BookingErrorCode.InvalidSession) {
				setBooking(undefined);
			} else {
				localStorage.setItem(LS_ERROR_CODE, JSON.stringify(errorCode));
			}

			scrollToTop();
		}
	}, [errorCode]);

	React.useEffect(() => {
		if (!firstRender) {
			if (location.pathname === BOOKING) {
				resetSavedData();
			}

			if (errorCode) {
				redirect();
			}
		}
	}, [location.pathname]);

	const value: BookingContextProps = {
		state: {
			item: booking,
			errorCode,
			existingPetEntry,
			selectedOption,
			isLoadingReserve,
			errorMessageReserve,
		},
		action: {
			setItem: (val) => setBooking(val),
			setErrorCode: (code) => setErrorCode(code),
			setExistingPetEntry: (val) => setExistingPetEntry(val),
			setSelectedOption: (val) => setSelectedOption(val),
			setIsLoadingReserve: (bool) => setIsLoadingReserve(bool),
			setErrorMessageReserve: (str) => setErrorMessageReserve(str),
		},
	};

	return (
		<BookingContext.Provider value={value}>
			<ScrollToTop />

			{errorCode !== null && !errorCodeNotEndingSession.some((code) => code === errorCode) ? (
				<BookingErrorScreen />
			) : !booking?.guid ? (
				<div className="online-booking">
					<div className="container bg-white">
						<div className="center-center-content">
							<div className="online-booking__main">
								<h1>Schedule an Appointment</h1>
								<div className="subtitle">Search by zip code to find a Lap of Love doctor.</div>
								<BookingLoader
									loading={loading}
									errorMessage={error}
									onClick={checkZipCode}
									removeIfError={false}
								>
									<>
										<div className="search">
											<SearchField
												type="tel"
												placeholder="Enter your 5-digit ZIP code"
												iconLocation
												widthBorder="0"
												searchBody="/booking"
												setSearch={setValueZip}
												newURLAfterSearch={false}
											/>
										</div>
										<div className="small-text small-text-wrap">
											<p>
												Your pet family's needs are important to us. To ensure the most seamless care and service, some
												appointments may require speaking with one of our compassionate Veterinary Care Coordinators directly
												to personalize your experience and confirm your booking.
											</p>
										</div>
									</>
								</BookingLoader>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="online-booking">
					<div className="container online-booking-information">
						<BookingStep />
						{
							booking.lastStep < OnlineBookingLastStep.Schedule
							&& errorCode !== BookingErrorCode.PetHasExistingAppointment
							&& <BookingFAQ />
						}
						<BookingReservAppointment />
					</div>
				</div>
			)}
		</BookingContext.Provider>
	);
};

const Booking: React.FC = () => {
	return (
		<HelmetProvider ignoreServerPage>
			<CustomRequestProvider>
				<BookingCheckZip />
			</CustomRequestProvider>
		</HelmetProvider>
	);
};

export default Booking;
